import { SHOP_REGISTRY } from '../data/shops.config';
import { GRIMOIRE_POOL } from '../data/loot-pools.config';
import { ITEMS } from '../data/inventory.config';
import { getUnlockedSpellTargets } from '../utils/item.utils';
import { useShopPurchasesStore } from '../store/shopPurchasesSlice';
import { useShopStockStore } from '../store/shopStockSlice';
import { useLoadoutStore } from '../store/loadoutSlice';
import { TalentEffects } from './TalentEffectsService';
import type { ShopOffer } from '../types/shop.types';

const FRUGAL_DISCOUNT = 0.85;
const DEFAULT_SHOP_ID = 'apothecary';

/**
 * Effective purchase price of an offer, after applying the Frugal talent
 * (−15% if taken). Minimum floor of 1 — a price never drops to 0.
 * Cf. `docs/talents.md` (utility notable Frugal).
 */
export function getItemPrice(offer: ShopOffer): number {
  const multiplier = TalentEffects.hasFrugal() ? FRUGAL_DISCOUNT : 1;
  return Math.max(1, Math.round(offer.priceGold * multiplier));
}

/** Fisher-Yates partial shuffle, then take the first `n` (or fewer). */
function pickDistinct<T>(pool: T[], n: number): T[] {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, Math.min(n, copy.length));
}

/**
 * Rolls a fresh random stock for a rolling shop: `consumableCount` consumables
 * drawn from the shop's `offers` pool + `grimoireCount` grimoires drawn from
 * `GRIMOIRE_POOL` (excluding grimoires whose target spell was already bought
 * anywhere). A `featuredTrinket` (if unowned) takes one consumable slot so the
 * total item count is constant. No-op if the shop has no `rolling` config.
 */
export function rollShopStock(shopId: string = DEFAULT_SHOP_ID): void {
  const shop = SHOP_REGISTRY[shopId];
  if (!shop?.rolling) {
    return;
  }
  const { consumableCount, grimoireCount, grimoirePrice, featuredTrinket } =
    shop.rolling;

  const featured: ShopOffer[] = [];
  if (
    featuredTrinket &&
    useLoadoutStore.getState().getMinorLevel(featuredTrinket.minorId) === null
  ) {
    featured.push({
      itemId: featuredTrinket.minorId,
      priceGold: featuredTrinket.priceGold,
      kind: 'minor',
    });
  }

  const consumables = pickDistinct(
    shop.offers,
    consumableCount - featured.length,
  );

  const purchased = useShopPurchasesStore.getState().purchasedSpellUnlocks;
  const grimoirePool = GRIMOIRE_POOL.filter((gid) => {
    const def = ITEMS[gid];
    if (!def) {
      return false;
    }
    const targets = getUnlockedSpellTargets(def);
    return (
      targets.length > 0 &&
      !targets.some((spellId) => purchased.includes(spellId))
    );
  });
  const grimoires: ShopOffer[] = pickDistinct(grimoirePool, grimoireCount).map(
    (gid) => ({ itemId: gid, priceGold: grimoirePrice }),
  );

  useShopStockStore
    .getState()
    .setStock([...featured, ...consumables, ...grimoires]);
}

/** Rolls an initial stock if the shop has never been stocked yet. */
export function ensureShopStock(shopId: string = DEFAULT_SHOP_ID): void {
  if (useShopStockStore.getState().offers.length === 0) {
    rollShopStock(shopId);
  }
}
